import React from "react";
import PropTypes from "prop-types";

// material ui
import { Button, Table, TableBody, TableCell, TableContainer, TableRow, TextField } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format, isValid } from "date-fns";

// components
import CustomToolbar from "../../../global/CustomToolbar";
import ErrorFeedbackBar from "../../../global/ErrorFeedbackBar";
import GlobalTableHead from "../../../global/Table/GlobalTableHead";

// constants
import { ASC, DESC } from "../../../global/constants";
import {
    CANCEL,
    DATE_FORMAT,
    DEFAULT_SORT_BY_HEAD_CELL,
    END_DATE_LABEL,
    MARKETPLACE_LABEL,
    NO_ORDERS_FOUND,
    ORDER_STATUS_LABEL,
    ORDER_STATUS_OPTIONS,
    START_DATE_LABEL,
    SUBMIT,
    TOTAL_ORDERS_FETCHED,
    WALMART_HEAD_CELLS,
    WALMART_MARKETPLACE_COMPANY,
    WALMART_TOOLBAR_TITLE,
} from "./constants";

// redux
import withShipment from "../../../../withShipment";
import { clearWalmartOrders, fetchWalmartOrdersByDateRange } from "../../../../redux/actions/admin";
import { walmartOrdersSelector } from "../../../../redux/selectors/admin";
import { listMarketplaces } from "../../../../redux/actions/settings";
import { marketplacesSelector } from "../../../../redux/selectors/settings";
import { userInfoSelector } from "../../../../redux/selectors/auth";
import { loadingSelector } from "../../../../redux/selectors/global";

import "./Walmart.css";

class Walmart extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            startDate: null,
            endDate: null,
            marketplace: null,
            orderStatus: null,
            order: ASC,
            orderBy: DEFAULT_SORT_BY_HEAD_CELL,
            submitted: false,
        }
    }

    componentDidMount() {
        this.props.clearWalmartOrders()
        this.props.listMarketplaces({
            id: this.props.userInfo.company
        })
    }

    getWalmartMarketplaces = () => {
        return (this.props.marketplaces || [])
            .filter((marketplace) => marketplace.marketplaceCompany === WALMART_MARKETPLACE_COMPANY && marketplace.enabled)
    }

    handleDateChange = (name, value) => {
        this.setState({
            [name]: value
        })
    }

    handleChange = (name, value) => {
        this.setState({
            [name]: value
        })
    }

    handleRequestSort = (event, property) => {
        const isAsc = this.state.orderBy === property && this.state.order === ASC;
        this.setState({
            order: isAsc ? DESC : ASC,
            orderBy: property,
        })
    }

    // The typed calendar date is sent as-is, so the server can expand it to a literal UTC
    // boundary. toISOString() is deliberately avoided here: it would shift the date by a day
    // for anyone west of UTC.
    formatDate = (date) => {
        // date-fns format() throws on an Invalid Date, so never call it unguarded
        return date && isValid(date) ? format(date, DATE_FORMAT) : null
    }

    // A partially typed date such as "2026-08-0" reaches onChange as an Invalid Date object, which is
    // truthy, so presence alone is not enough of a check here.
    isInvalidDate = (date) => {
        return date != null && !isValid(date)
    }

    isSubmitDisabled = () => {
        // end date is optional, so only its being invalid blocks submit, not its being empty
        return !this.state.startDate
            || this.isInvalidDate(this.state.startDate)
            || this.isInvalidDate(this.state.endDate)
            || !this.state.marketplace
            || !this.state.orderStatus
    }

    handleCancel = () => {
        this.props.clearWalmartOrders()
        this.setState({
            startDate: null,
            endDate: null,
            marketplace: null,
            orderStatus: null,
            submitted: false,
        })
    }

    handleSubmit = () => {
        this.setState({ submitted: true })
        this.props.fetchWalmartOrdersByDateRange({
            marketplaceId: this.state.marketplace.marketplaceId,
            orderStatus: this.state.orderStatus,
            startDate: this.formatDate(this.state.startDate),
            endDate: this.formatDate(this.state.endDate),
        })
    }

    sortOrders = (orders) => {
        const { order, orderBy } = this.state
        return [...orders].sort((a, b) => {
            const first = a[orderBy] ? a[orderBy] : ''
            const second = b[orderBy] ? b[orderBy] : ''
            if (first === second) {
                return 0
            }
            if (order === ASC) {
                return first < second ? -1 : 1
            }
            return first > second ? -1 : 1
        })
    }

    renderResults = () => {
        const orders = this.props.walmartOrders || []
        if (!this.state.submitted) {
            return null
        }
        if (orders.length === 0) {
            return <div className="walmart-empty">{NO_ORDERS_FOUND}</div>
        }
        return (
            <>
            <TableContainer component={Paper} className="walmart-table">
                <Table>
                    <GlobalTableHead
                        headCells={WALMART_HEAD_CELLS}
                        order={this.state.order}
                        orderBy={this.state.orderBy}
                        onRequestSort={this.handleRequestSort}
                    />
                    <TableBody>
                        {this.sortOrders(orders).map((order, index) => (
                            <TableRow key={`${order.orderId}-${index}`}>
                                {/* matches the leading checkbox cell GlobalTableHead renders */}
                                <TableCell padding="checkbox" />
                                <TableCell align="center">{order.orderId}</TableCell>
                                <TableCell align="center">{order.purchaseOrderId}</TableCell>
                                <TableCell align="center">{order.orderStatus}</TableCell>
                                <TableCell />
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className="walmart-total">{`${TOTAL_ORDERS_FETCHED}: ${orders.length}`}</div>
            </>
        )
    }

    render() {
        return (
            <>
                <ErrorFeedbackBar />
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <CustomToolbar
                        title={WALMART_TOOLBAR_TITLE}
                        headerButtons={[]}
                    />
                    {this.props.isLoading ?
                        <div className="getPrinterLoadingOuterBox">
                            <div className="getPrinterLoadingBox">
                                <CircularProgress size={100} />
                            </div>
                        </div> :
                        <>
                            <div className="walmart-container">
                                <div className="walmart-field">
                                    <KeyboardDatePicker
                                        autoOk
                                        name="walmart-start-date"
                                        variant="inline"
                                        inputVariant="outlined"
                                        label={START_DATE_LABEL}
                                        format={DATE_FORMAT}
                                        placeholder={DATE_FORMAT}
                                        value={this.state.startDate}
                                        InputAdornmentProps={{ position: "start" }}
                                        onChange={(date) => this.handleDateChange("startDate", date)}
                                        style={{ width: 220 }}
                                    />
                                </div>
                                <div className="walmart-field">
                                    <KeyboardDatePicker
                                        autoOk
                                        clearable
                                        name="walmart-end-date"
                                        variant="inline"
                                        inputVariant="outlined"
                                        label={END_DATE_LABEL}
                                        format={DATE_FORMAT}
                                        placeholder={DATE_FORMAT}
                                        value={this.state.endDate}
                                        InputAdornmentProps={{ position: "start" }}
                                        onChange={(date) => this.handleDateChange("endDate", date)}
                                        style={{ width: 220 }}
                                    />
                                </div>
                                <div className="walmart-field">
                                    <Autocomplete
                                        options={this.getWalmartMarketplaces()}
                                        getOptionLabel={(option) => option.marketplaceName ? option.marketplaceName : ''}
                                        value={this.state.marketplace}
                                        onChange={(event, value) => this.handleChange("marketplace", value)}
                                        style={{ width: 220 }}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label={MARKETPLACE_LABEL}
                                                variant="outlined"
                                            />
                                        }
                                    />
                                </div>
                                <div className="walmart-field">
                                    <Autocomplete
                                        options={ORDER_STATUS_OPTIONS}
                                        getOptionLabel={(option) => option}
                                        value={this.state.orderStatus}
                                        onChange={(event, value) => this.handleChange("orderStatus", value)}
                                        style={{ width: 220 }}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label={ORDER_STATUS_LABEL}
                                                variant="outlined"
                                            />
                                        }
                                    />
                                </div>
                            </div>
                            <div className="walmart-buttons">
                                <Button
                                    variant="contained"
                                    onClick={this.handleCancel}
                                >
                                    {CANCEL}
                                </Button>
                                <Button
                                    style={{ marginLeft: 10 }}
                                    color="primary"
                                    variant="contained"
                                    disabled={this.isSubmitDisabled()}
                                    onClick={this.handleSubmit}
                                >
                                    {SUBMIT}
                                </Button>
                            </div>
                            {this.renderResults()}
                        </>
                    }
                </MuiPickersUtilsProvider>
            </>
        )
    }
}

const mapStateToProps = (state) => ({
    walmartOrders: walmartOrdersSelector(state),
    marketplaces: marketplacesSelector(state),
    userInfo: userInfoSelector(state),
    isLoading: loadingSelector(state),
})

const actionCreators = {
    fetchWalmartOrdersByDateRange,
    clearWalmartOrders,
    listMarketplaces,
}

Walmart.propTypes = {
    walmartOrders: PropTypes.array,
    marketplaces: PropTypes.array,
    userInfo: PropTypes.object,
    isLoading: PropTypes.bool,
}

Walmart.defaultProps = {
    walmartOrders: [],
    marketplaces: [],
    userInfo: {},
    isLoading: false,
}

export default withShipment({
    mapStateToProps,
    actionCreators
}, Walmart);
